import { ColumnTable, Table } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';

import { SectionTitle } from '../../../../../components/text';
import { TranslationsKeys } from '../../../../../locales/translations';
import { useAcademicOfferWizard } from '../../context/AcademicOfferWizardContext';
import {
  NonStructuredAcademicOffer,
  NonStructuredType,
  SummarySectionRow,
} from '../../types';
import { AcademicOfferRequiredDataForm } from './AcademicOfferRequiredDataForm';
import { useAcademicOfferSummary } from './loaders/AcademicOfferSummaryLoader';

interface SummaryData {
  programName: string;
  programType: string;
  programCode: string;
  modality: string;
  deliveryMode: string | null;
  school: string;
  campus: string;
  quota: string;
  completionDaysLimit: string;
}

function extractSummaryData(
  offer: NonStructuredAcademicOffer,
  t: (key: string) => string,
): SummaryData {
  const aospvo = offer.academicOfferStudyPlanVersionOffers?.[0];
  const spvo = aospvo?.studyPlanVersionOffer;
  const studyPlan = spvo?.studyPlanVersion?.studyPlan;
  const ceType = studyPlan?.studyPlanCe?.type;

  const programTypeLabels: Record<NonStructuredType, string> = {
    [NonStructuredType.COURSE]: t('summary.programType.course'),
    [NonStructuredType.PROGRAM_COURSE]: t('summary.programType.program'),
  };

  return {
    programName: studyPlan?.name ?? '-',
    programType: ceType ? programTypeLabels[ceType] ?? '-' : '-',
    programCode: studyPlan?.code ?? '-',
    modality: studyPlan?.modality?.name ?? '-',
    deliveryMode: studyPlan?.modality?.deliveryMode ?? null,
    school: studyPlan?.school?.name ?? '-',
    campus: spvo?.campus?.name ?? '-',
    quota: aospvo?.quota != null ? aospvo.quota.toString() : '-',
    completionDaysLimit:
      aospvo?.completionDaysLimit != null
        ? aospvo.completionDaysLimit.toString()
        : '-',
  };
}

function AcademicOfferSummaryContent() {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const { ceAcademicOfferId } = useAcademicOfferWizard();
  const { data: summaryResponse } = useAcademicOfferSummary();

  const summaryData = useMemo(() => {
    if (!summaryResponse?.offer) {
      return null;
    }
    return extractSummaryData(summaryResponse.offer, t);
  }, [summaryResponse, t]);

  const sectionRows: SummarySectionRow[] = useMemo(
    () => summaryResponse?.sections ?? [],
    [summaryResponse],
  );

  const primaryBorderTh = useMemo(
    () => ({
      style: { borderBottom: '1px solid var(--primary)' },
    }),
    [],
  );

  const sectionColumns = useMemo<ColumnTable<SummarySectionRow>[]>(
    () => [
      {
        columnName: 'subjectName',
        headerText: t('summary.sections.subject'),
        thClassName: 'text-left',
        tdClassName: 'text-left',
        thProps: primaryBorderTh,
        width: '34%',
      },
      {
        columnName: 'sectionName',
        headerText: t('summary.sections.sectionName'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        thProps: primaryBorderTh,
        width: '33%',
      },
      {
        columnName: 'modality',
        headerText: t('summary.sections.modality'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        thProps: primaryBorderTh,
        width: '33%',
      },
    ],
    [t, primaryBorderTh],
  );

  if (!summaryData || !ceAcademicOfferId) {
    return null;
  }

  return (
    <>
      <SectionTitle text={t('summary.title')} />
      <p>{t('summary.description')}</p>

      {/* Información principal y Datos adicionales en cards */}
      <Row className="mt-3 mb-4">
        <Col xs={12} md={6} className="mb-3">
          <Card
            className="h-100"
            style={{ border: '1px solid var(--primary)' }}
          >
            <CardHeader
              className="bg-white"
              style={{ borderBottom: '1px solid var(--primary)' }}
            >
              <span
                className="fw-700 text-primary"
                style={{ fontSize: '16px' }}
              >
                {t('summary.mainInfo')}
              </span>
            </CardHeader>
            <CardBody>
              <p className="mb-2">
                <span className="fw-700">{t('summary.programName')}:</span>{' '}
                {summaryData.programName}
              </p>
              <p className="mb-2">
                <span className="fw-700">
                  {t('summary.programType.label')}:
                </span>{' '}
                {summaryData.programType}
              </p>
              <p className="mb-0">
                <span className="fw-700">{t('summary.programCode')}:</span>{' '}
                {summaryData.programCode}
              </p>
            </CardBody>
          </Card>
        </Col>
        <Col xs={12} md={6} className="mb-3">
          <Card
            className="h-100"
            style={{ border: '1px solid var(--primary)' }}
          >
            <CardHeader
              className="bg-white"
              style={{ borderBottom: '1px solid var(--primary)' }}
            >
              <span
                className="fw-700 text-primary"
                style={{ fontSize: '16px' }}
              >
                {t('summary.additionalData')}
              </span>
            </CardHeader>
            <CardBody>
              <p className="mb-2">
                <span className="fw-700">{t('summary.modality')}:</span>{' '}
                {summaryData.modality}
              </p>
              <p className="mb-2">
                <span className="fw-700">{t('summary.school')}:</span>{' '}
                {summaryData.school}
              </p>
              <p className="mb-2">
                <span className="fw-700">{t('summary.campus')}:</span>{' '}
                {summaryData.campus}
              </p>
              <p className="mb-2">
                <span className="fw-700">{t('summary.quota')}:</span>{' '}
                {summaryData.quota}
              </p>
              <p className="mb-0">
                <span className="fw-700">
                  {t('summary.completionDaysLimit')}:
                </span>{' '}
                {summaryData.completionDaysLimit}
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Secciones */}
      <div className="mb-4">
        <SectionTitle text={t('summary.sections.title')} />
        <div
          style={{
            border: '1px solid var(--primary)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <Table<SummarySectionRow>
            columns={sectionColumns}
            data={sectionRows}
            isLoadingResults={false}
            noResultsText={t('summary.sections.empty')}
            color="transparent"
          />
        </div>
      </div>

      {/* Datos requeridos para completar la oferta */}
      <div className="mt-4">
        <AcademicOfferRequiredDataForm
          deliveryMode={summaryData.deliveryMode}
        />
      </div>
    </>
  );
}

export { AcademicOfferSummaryContent };
